import { useState, useEffect } from 'react';
import {Dialog, DialogTitle, DialogContent, Typography, TextField, DialogActions, Button} from '@mui/material';
import { EditDialogProps } from '../../supplier/currentInventoryReport/data';
import {DialogContentContainer, DialogInputContainer, InputFieldContainer, InputSection, ValidatedInputFieldContainer} from './styles';
import { AssetTypeAutocomplete } from './autocomplete/AssetTypeAutocomplete';
import { AssetSizeAutocomplete } from './autocomplete/AssetSizeAutocomplete';
import { validateContainerNumber } from '../../../util/ContainerValidator';

const EditAssetDialog = (props:EditDialogProps) => {
    const { open, asset, onClose, handleEdit } = props;

    const [assetType, setAssetType] = useState<string>();
    const [assetSize, setAssetSize] = useState<string>('');
    
    const [containerNumber, setContainerNumber] = useState<string>();
    const [chassisNumber, setChassisNumber] = useState<string>();
    const [chassisLicensePlateNumber, setChassisLicensePlateNumber] = useState<string>();
    const [truckLicensePlateNumber, setTruckLicensePlateNumber] = useState<string>();
    const [trailerNumber, setTrailerNumber] = useState<string>();
    const [scacCode, setScacCode] = useState<string>();
    const [sealNumber, setSealNumber] = useState<string>();
    const [driverFirstName, setDriverFirstName] = useState<string>();
    const [driverLastName, setDriverLastName] = useState<string>();
    const [driverLicenseNumber, setDriverLicenseNumber] = useState<string>();
    const [notes, setNotes] = useState<string>();

    const [containerError, setContainerError] = useState<string>();

    useEffect(() => {
        if(asset) {
            setAssetType(asset.assetType)
            setAssetSize(asset.assetSize)
            setContainerNumber(asset.containerNumber)
            setChassisNumber(asset.chassisNumber)
            setChassisLicensePlateNumber(asset.chassisLicensePlateNumber)
            setTruckLicensePlateNumber(asset.truckLicensePlateNumber)
            setTrailerNumber(asset.trailerNumber)
            setSealNumber(asset.sealNumber)
            setScacCode(asset.scacCode)
            setDriverFirstName(asset.driverFirstName)
            setDriverLastName(asset.driverLastName)
            setDriverLicenseNumber(asset.driverLicenseNumber)
            setNotes(asset.notes)
        }
    }, [asset])

    const handleAssetTypeChange = (_: any, value: string) => {
        if(value) {
            setAssetType(value);
        }
    }

    const handleAssetSizeChange = (_: any, value: {id: string, label: string}) => {
        if(value) {
            setAssetSize(value.id);
        }
    }

    const handleContainerFieldChange = (e: {target: {value: string}}) => {
        const {value} = e.target;
        if (value && !validateContainerNumber(value)) {
            setContainerError('Container number is invalid')
        } else {
            setContainerError('')
        }
        setContainerNumber(value)
    }

    const handleTextFieldChange = (e: {target: {value: string}}, onChange: (value: string) => void) => {
        const {value} = e.target;
        onChange(value);
    }

    const handleClose = () => {
        setAssetType("")
        setAssetSize('')
        setContainerNumber("")
        setChassisNumber("")
        setChassisLicensePlateNumber("")
        setTruckLicensePlateNumber("")
        setScacCode("")
        setDriverFirstName("")
        setDriverLastName("")
        setDriverLicenseNumber("")
        setSealNumber("")
        setNotes("")

        onClose();
    }

    const handleSubmit = () => {
        if(asset) {
            const {id, locationId} = asset;

            const editedAsset = {
                id,
                locationId,
                containerNumber,
                chassisNumber,
                chassisLicensePlateNumber,
                sealNumber,
                driverFirstName,
                driverLastName,
                driverLicenseNumber,
                trailerNumber,
                truckLicensePlateNumber,
                scacCode,
                notes,
                assetSize,
                assetType,
            }
            handleEdit(editedAsset);
            onClose();
        }
    }

    return <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle variant={'h6'}>Edit Equipment</DialogTitle>
            <DialogContent>
                {
                    asset && <DialogContentContainer>
                        <DialogInputContainer>
                            <AssetTypeAutocomplete 
                                value={assetType} 
                                onChange={handleAssetTypeChange}
                            />
                            <AssetSizeAutocomplete 
                                value={assetSize} 
                                onChange={handleAssetSizeChange}
                            />
                        </DialogInputContainer>
                        <DialogInputContainer>
                            <ValidatedInputFieldContainer>
                                <Typography width='70%'>Container Number</Typography>
                                <TextField
                                    name='containerNumber'
                                    onChange={handleContainerFieldChange}
                                    value={containerNumber}
                                    variant='standard'
                                    helperText={containerError}
                                    FormHelperTextProps={{error: true}}
                                    fullWidth
                                />
                            </ValidatedInputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>Chassis Number</Typography>
                                <TextField
                                    name='chassisNumber'
                                    onChange={(e) => handleTextFieldChange(e, setChassisNumber)}
                                    value={chassisNumber}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>Trailer Number</Typography>
                                <TextField
                                    name='trailerNumber'
                                    onChange={(e) => handleTextFieldChange(e, setTrailerNumber)}
                                    value={trailerNumber}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>Truck License Plate Number</Typography>
                                <TextField
                                    name='truckLicensePlateNumber'
                                    onChange={(e) => handleTextFieldChange(e, setTruckLicensePlateNumber)}
                                    value={truckLicensePlateNumber}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>Chassis License Plate Number</Typography>
                                <TextField
                                    name='chassisLicensePlateNumber'
                                    onChange={(e) => handleTextFieldChange(e, setChassisLicensePlateNumber)}
                                    value={chassisLicensePlateNumber}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>Seal Number</Typography>
                                <TextField
                                    name='sealNumber'
                                    onChange={(e) => handleTextFieldChange(e, setSealNumber)}
                                    value={sealNumber}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>SCAC Code</Typography>
                                <TextField
                                    name='scacCode'
                                    onChange={(e) => handleTextFieldChange(e, setScacCode)}
                                    value={scacCode}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                        </DialogInputContainer>
                        <DialogInputContainer>
                                <InputSection>
                                    <InputFieldContainer>
                                        <Typography width='70%'>Driver First Name</Typography>
                                        <TextField
                                            name='driverFirst'
                                            onChange={(e) => handleTextFieldChange(e, setDriverFirstName)}
                                            value={driverFirstName}
                                            variant='standard'
                                            fullWidth
                                        />
                                    </InputFieldContainer>
                                    <InputFieldContainer>
                                        <Typography width='70%'>Driver Last Name</Typography>
                                        <TextField
                                            name='driverLast'
                                            onChange={(e) => handleTextFieldChange(e, setDriverLastName)}
                                            value={driverLastName}
                                            variant='standard'
                                            fullWidth
                                        />
                                    </InputFieldContainer>
                                    <InputFieldContainer>
                                        <Typography width='70%'>Driver License Number</Typography>
                                        <TextField
                                            name='driverLicenseNumber'
                                            onChange={(e) => handleTextFieldChange(e, setDriverLicenseNumber)}
                                            value={driverLicenseNumber}
                                            variant='standard'
                                            fullWidth
                                        />
                                    </InputFieldContainer>
                                </InputSection>
                                <InputFieldContainer>
                                    <Typography width='70%'>Notes</Typography>
                                    <TextField
                                        name='notes'
                                        onChange={(e) => handleTextFieldChange(e, setNotes)}
                                        value={notes}
                                        variant='standard'
                                        fullWidth
                                    />
                                </InputFieldContainer>
                        </DialogInputContainer>
                    </DialogContentContainer>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={handleSubmit}>Save Changes</Button>
                <Button onClick={handleClose}>Exit</Button>
            </DialogActions>
        </Dialog>
}

export default EditAssetDialog;